/**
 * @fileoverview The mailbox rental agreement vocabulary (MAILBOX-SIGNUP-JOURNEY-001 B3 / D1): an agreement's lifecycle
 * (`mailbox_agreements.status`), where a store's terms came from and their language
 * (`mailbox_agreement_templates.source` / `.language`), and how a signature was given and its copy offered
 * (`mailbox_agreement_signatures.method` / `.copyChannel`). Declared once for pos-api (the records, the signing and the
 * renewal rule) and the web (pos-web's "awaiting signature" badge, the customer display), so no app spells an
 * agreement's state for itself.
 *
 * ## The agreement machine
 *
 * An agreement is created `awaiting_signature` at the rental's start (owner ruling O6: the rental starts, and the
 * badge shows until it is signed). It is `signed` (on screen or on paper) or `void` (the rental never took). A renewal
 * that needs a new signature (owner ruling O7: only when the terms version, the rate or the period changed) makes a NEW
 * agreement, and the one it replaces becomes `superseded`. Superseded and void are history. Every status is a KEY, a
 * terminal one mapping to `[]`, the `SHIPMENT_STATUS_TRANSITIONS` convention; pos `20261010198370`'s guard refuses any
 * other move in the database.
 *
 * @module @puffin-core/status-taxonomy/mailboxAgreement
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

/** The empty answer, shared so a caller can never widen a status's targets through it. */
const NO_TARGETS = Object.freeze([]);

/**
 * A rental agreement's status.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_AGREEMENT_STATUS = Object.freeze({
  AWAITING_SIGNATURE: 'awaiting_signature',
  SIGNED: 'signed',
  SUPERSEDED: 'superseded',
  VOID: 'void',
});

/** @type {ReadonlyArray<string>} */
const MAILBOX_AGREEMENT_STATUS_VALUES = Object.freeze(Object.values(MAILBOX_AGREEMENT_STATUS));

const { AWAITING_SIGNATURE, SIGNED, SUPERSEDED, VOID } = MAILBOX_AGREEMENT_STATUS;

/**
 * Every legal agreement move: an open agreement is signed or voided; only a signed one is superseded.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const MAILBOX_AGREEMENT_STATUS_TRANSITIONS = Object.freeze({
  [AWAITING_SIGNATURE]: Object.freeze([SIGNED, VOID]),
  [SIGNED]: Object.freeze([SUPERSEDED]),
  [SUPERSEDED]: NO_TARGETS,
  [VOID]: NO_TARGETS,
});

/**
 * An agreement's badge TONE (`<StatusBadgeCell statusTones>`, Q-MI-25): an unsigned agreement needs the counter's
 * attention, a signed one is good, and history is closed without a verdict.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_AGREEMENT_STATUS_BADGE_TONES = Object.freeze({
  [AWAITING_SIGNATURE]: BADGE_TONE.WARNING,
  [SIGNED]: BADGE_TONE.SUCCESS,
  [SUPERSEDED]: BADGE_TONE.NEUTRAL,
  [VOID]: BADGE_TONE.NEUTRAL,
});

/**
 * The statuses an agreement may move to from `from`.
 *
 * @param {string} from
 * @returns {ReadonlyArray<string>} The shared empty answer for a terminal or unknown status.
 */
function mailboxAgreementStatusTargets(from) {
  return MAILBOX_AGREEMENT_STATUS_TRANSITIONS[from] ?? NO_TARGETS;
}

/**
 * Where a store's terms version came from: the platform's starter text, the store's own text, or PostalMate's
 * MBCONTRACT (I3's import).
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_AGREEMENT_TEMPLATE_SOURCE = Object.freeze({
  STARTER: 'starter',
  STORE: 'store',
  POSTALMATE: 'postalmate',
});

/** @type {ReadonlyArray<string>} */
const MAILBOX_AGREEMENT_TEMPLATE_SOURCE_VALUES = Object.freeze(
  Object.values(MAILBOX_AGREEMENT_TEMPLATE_SOURCE),
);

/**
 * The languages a store publishes its terms in, each as its own version (Q-B3-1: English required, Spanish optional;
 * a customer signs the language they choose among the store's published ones).
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_AGREEMENT_LANGUAGE = Object.freeze({
  EN: 'en',
  ES: 'es',
});

/** @type {ReadonlyArray<string>} */
const MAILBOX_AGREEMENT_LANGUAGE_VALUES = Object.freeze(Object.values(MAILBOX_AGREEMENT_LANGUAGE));

/**
 * How a signature was given: on the paired customer display, or on the printed agreement at the counter.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_AGREEMENT_SIGNATURE_METHOD = Object.freeze({
  ON_SCREEN: 'on_screen',
  PAPER: 'paper',
});

/** @type {ReadonlyArray<string>} */
const MAILBOX_AGREEMENT_SIGNATURE_METHOD_VALUES = Object.freeze(
  Object.values(MAILBOX_AGREEMENT_SIGNATURE_METHOD),
);

/**
 * How the signer's copy was offered (ESIGN: a copy the signer can keep), or that they declined it.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_AGREEMENT_COPY_CHANNEL = Object.freeze({
  EMAIL: 'email',
  TEXT: 'text',
  PRINT: 'print',
  DECLINED: 'declined',
});

/** @type {ReadonlyArray<string>} */
const MAILBOX_AGREEMENT_COPY_CHANNEL_VALUES = Object.freeze(
  Object.values(MAILBOX_AGREEMENT_COPY_CHANNEL),
);

module.exports = {
  MAILBOX_AGREEMENT_STATUS,
  MAILBOX_AGREEMENT_STATUS_VALUES,
  MAILBOX_AGREEMENT_STATUS_TRANSITIONS,
  MAILBOX_AGREEMENT_STATUS_BADGE_TONES,
  mailboxAgreementStatusTargets,
  MAILBOX_AGREEMENT_TEMPLATE_SOURCE,
  MAILBOX_AGREEMENT_TEMPLATE_SOURCE_VALUES,
  MAILBOX_AGREEMENT_LANGUAGE,
  MAILBOX_AGREEMENT_LANGUAGE_VALUES,
  MAILBOX_AGREEMENT_SIGNATURE_METHOD,
  MAILBOX_AGREEMENT_SIGNATURE_METHOD_VALUES,
  MAILBOX_AGREEMENT_COPY_CHANNEL,
  MAILBOX_AGREEMENT_COPY_CHANNEL_VALUES,
};
